import { reactive, ref, watch } from 'vue';
import { OrgBudget, TeamBudgetOverview } from '@/types/budgets';
import { useMainStore } from '@/stores/mainStore';
import type { BudgetForTransform } from '@/utils/budgetHelpers';
import { isCategorizedBudget } from '@/utils/budgetHelpers';
import type { ApiTeamBudgetOverviewCategorized } from '@/types/budgets.categorized';
import type { TeamBudget } from '@/types/budgetGroups';

export const DIALOG_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
  DUPLICATE: 'duplicate',
} as const;

export type DialogType = typeof DIALOG_TYPES[keyof typeof DIALOG_TYPES];

type BudgetDialogBudget = TeamBudgetOverview | OrgBudget | ApiTeamBudgetOverviewCategorized | TeamBudget & { teamTwrn: string };

interface UseBudgetDialogState {
  isOpen: boolean;
  onSuccess: (() => void) | undefined;
  budgetTwrn?: string;
  budget?: BudgetDialogBudget;
  isCreating?: boolean;
  isDuplicating?: boolean;
  isEditing?: boolean;
}

const DEFAULT_STATE: UseBudgetDialogState = {
  isOpen: false,
  onSuccess: undefined,
  budgetTwrn: undefined,
  isCreating: false,
  isDuplicating: false,
  isEditing: false,
  budget: undefined,
};

const getDefaultState = () => ({ ...DEFAULT_STATE });

const state = reactive<UseBudgetDialogState>(getDefaultState());

function resetState() {
  Object.assign(state, getDefaultState());
}

const isRendered = ref<boolean>(false);

watch(() => state.isOpen, (isOpen) => {
  if (isOpen && !isRendered.value) isRendered.value = true;
  if (!isOpen) resetState();
});

interface UseBudgetDialogOptions {
  onSuccess?: () => void;
}

export function useBudgetDialog(options?: UseBudgetDialogOptions) {
  function getBudgetFormFields(budget: BudgetForTransform) {
    const mainStore = useMainStore();

    if (budget instanceof OrgBudget) {
      return OrgBudget.mapBudgetToFormFields(budget);
    }

    if (budget instanceof TeamBudgetOverview) {
      return TeamBudgetOverview.mapBudgetToFormFields(budget, mainStore.teamTwrn || undefined);
    }

    // Categorized format: transformBudgetForForm will handle conversion
    if (isCategorizedBudget(budget)) {
      return {
        name: budget.name,
        academicYear: undefined,
        team: budget.team_twrn || budget.team?.twrn || mainStore.teamTwrn,
        totalBudget: undefined,
        scholarships: undefined,
        scholarshipValue: undefined,
        rosterSize: undefined,
        thirdPartyTarget: undefined,
      };
    }

    // This should never be reached - all budgets are either instances or categorized
    return {
      name: undefined,
      academicYear: undefined,
      team: mainStore.teamTwrn,
      totalBudget: undefined,
      scholarships: undefined,
      scholarshipValue: undefined,
      rosterSize: undefined,
      thirdPartyTarget: undefined,
    };
  }

  async function open(type: DialogType = DIALOG_TYPES.CREATE, budget?: BudgetDialogBudget) {
    if (type === DIALOG_TYPES.CREATE) {
      state.isCreating = true;
    } else if (type === DIALOG_TYPES.EDIT) {
      state.isEditing = true;
    } else if (type === DIALOG_TYPES.DUPLICATE) {
      state.isDuplicating = true;
    }
    state.isOpen = true;
    state.onSuccess = options?.onSuccess;
    state.budgetTwrn = budget?.twrn || (budget as any)?.twrn;
    state.budget = budget;
  }

  function updateBudget(budget: BudgetDialogBudget) {
    state.budget = budget;
  }

  return {
    state,
    isRendered,
    resetState,
    open,
    getBudgetFormFields,
    updateBudget,
  };
}
